import type { TileKind } from './types';
import { TILE_KINDS } from './types';
import { Board } from './board';

export type GoalDef =
  | { type: 'collect'; kind: TileKind; count: number }
  /** clear every mud block on the board */
  | { type: 'mud' }
  /** clear every paw stain on the board */
  | { type: 'stain' };

export const LOCATIONS = ['cafe', 'paris'] as const;
export type Location = (typeof LOCATIONS)[number];

/** Bot results written by `npm run levels:calibrate` – information only, the game ignores it. */
export interface Calibration {
  runs: number;
  /** share of bot games won, 0–1 */
  winRate: number;
  /** average moves left when the bot won */
  movesLeft: number;
  /** share of games lost with at most 3 goal units missing */
  closeLosses: number;
  /** the win rate the generator aimed for */
  target: number;
}

/**
 * Level file format: levels/level-NNN.json. Edit by hand, with
 * `npm run level` or with the web editor (editor.html).
 */
export interface LevelDef {
  id: number;
  moves: number;
  /** tile kinds in play: a number takes the first n of TILE_KINDS, or an explicit list */
  kinds: number | TileKind[];
  goals: GoalDef[];
  /**
   * Optional layout, one string per row (see Board.parse for the codes).
   * '.' cells are filled at random. Without a layout: empty 8×8 board.
   */
  board?: string[];
  /** score for 2 and 3 stars (1 star = level won). Default: moves × 60 / × 100 */
  stars?: [number, number];
  /** fixed seed for a reproducible board (tutorials); otherwise the game passes one */
  seed?: number;
  /** where on the world trip this level is played (background, map section) */
  location?: Location;
  /** 'hard' levels are tuned for many narrow losses */
  difficulty?: 'easy' | 'normal' | 'hard';
  calibration?: Calibration;
}

export function levelKinds(def: LevelDef): TileKind[] {
  return typeof def.kinds === 'number' ? TILE_KINDS.slice(0, def.kinds) : [...def.kinds];
}

/** Checks a level file; returns human-readable problems (empty = fine). */
export function validateLevel(def: LevelDef): string[] {
  const errors: string[] = [];
  if (!Number.isInteger(def.id) || def.id < 1) errors.push('id must be a positive whole number');
  if (!Number.isInteger(def.moves) || def.moves < 1 || def.moves > 99) errors.push('moves must be 1–99');
  const kinds = levelKinds(def);
  if (kinds.length < 3 || kinds.length > 6 || kinds.some((k) => !TILE_KINDS.includes(k))) errors.push('kinds must be 3–6 valid tile kinds');
  if (!def.goals?.length) errors.push('at least one goal is needed');
  let board: Board | null = null;
  if (def.board) {
    try {
      board = Board.parse(def.board);
    } catch (e) {
      errors.push(`board: ${(e as Error).message}`);
    }
  }
  for (const g of def.goals ?? []) {
    if (g.type === 'collect') {
      if (!kinds.includes(g.kind)) errors.push(`goal collects ${g.kind}, which is not in play`);
      if (!Number.isInteger(g.count) || g.count < 1) errors.push('collect count must be ≥ 1');
    } else if (g.type === 'mud' && !board?.count((c) => !!c.blocker)) errors.push('mud goal but no mud on the board');
    else if (g.type === 'stain' && !board?.count((c) => c.stain)) errors.push('stain goal but no paw stains on the board');
  }
  if (def.stars && !(def.stars[0] <= def.stars[1])) errors.push('stars must be [two-star score, three-star score]');
  return errors;
}
